/**
 * `POST /sym/topdown/levels` — a TopDown screen's Sector and Industry rows
 * (`docs/features/topdown.md`).
 *
 * qv-data builds one index per group from the members sent (the base screen's
 * rows after its filter) and evaluates every level column on its bars. Same
 * service and base URL as `/sym/*` (`api/symbols.ts`), and the same rule:
 * nothing here throws — a failure is `{ ok: false, reason }` for a notice.
 */

import type { ScreenerColumn } from "@qufolio/model";

import { requestHeaders } from "../lib/user";
import type { LevelKey, LevelRow, MemberRow } from "../lib/topdown";
import { httpBase } from "./backend";

const SYM_URL = (
  httpBase(import.meta.env.VITE_QV_SYM_URL, import.meta.env.VITE_QV_SET_URL) || ""
).replace(/\/$/, "");

export const topDownConfigured = SYM_URL !== "";

export interface LevelsAnswer {
  levels: Partial<Record<LevelKey, LevelRow[]>>;
  issues: { kind: string; name: string; message: string }[];
}

export type LevelsResult = { ok: true; answer: LevelsAnswer } | { ok: false; reason: string };

export async function fetchLevels(
  members: readonly MemberRow[],
  levels: readonly { key: LevelKey; columns: ScreenerColumn[] }[],
  signal?: AbortSignal,
  weighting: "equal" | "cap" = "equal",
): Promise<LevelsResult> {
  if (!SYM_URL) return { ok: false, reason: "no server configured" };
  try {
    const response = await fetch(`${SYM_URL}/sym/topdown/levels`, {
      method: "POST",
      headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify({ members, levels, weighting }),
      signal,
    });
    if (!response.ok) {
      return { ok: false, reason: (await response.text()) || `levels failed (${response.status})` };
    }
    return { ok: true, answer: (await response.json()) as LevelsAnswer };
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") return { ok: false, reason: "aborted" };
    return { ok: false, reason: cause instanceof Error ? cause.message : "levels failed" };
  }
}
